import React, { FC, useMemo } from 'react';
import { darken } from 'color2k';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { Flex } from '$components/layouts';
import { Button } from '$components/elements/button';
import { SearchContentTypes } from '~/modules/search';
import { useTranslation } from '$hooks';
import { Text } from '$components/elements/text';
import { Headline } from '$components/elements/headline';
import { Link } from '$components/elements/link';
import { IHelloRetailSearchWordSuggestion } from '~/modules/search/interfaces/suggestion';
import { RouteKeys, useCMSRoute } from '~/store/routes';
import { trackHelloRetailClick } from '$services/hello-retail-tracking.service';
import { SvgIcon } from '$components/elements/svg-icon';
import { formatString, mq } from '$lib/helpers';

type Props = {
    currentType?: SearchContentTypes;
    hasTypeSelection?: boolean;
    tabCount?: { [key in SearchContentTypes]?: number };
    resultListRefs?: { [key in SearchContentTypes]?: React.RefObject<HTMLDivElement> };
    totalResults?: number;
    searchTerm?: string;
    searchWordSuggestions?: IHelloRetailSearchWordSuggestion[];
    hasNoResults?: boolean;
};

export const CombinedSearchHeader: FC<Props> = ({
    currentType,
    hasTypeSelection,
    tabCount,
    totalResults,
    searchTerm,
    searchWordSuggestions,
    resultListRefs,
    hasNoResults,
}: Props) => {
    const { translate } = useTranslation();
    const { routes } = useCMSRoute();
    const { back } = useRouter();

    const noTypeResults = useMemo(() => {
        return Object.values(tabCount || {}).reduce((acc, count) => acc + count, 0) === 0;
    }, [tabCount]);

    const scrollTo = (ref?: React.RefObject<HTMLDivElement>) => {
        if (ref?.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const filteredSearchWordSuggestions = useMemo(() => {
        return searchWordSuggestions?.filter((word) => word.title?.toLowerCase() !== searchTerm?.toLowerCase());
    }, [searchWordSuggestions, searchTerm]);

    const getSerchWordUrl = (searchWord?: string) => {
        const searchResultUrl = routes[RouteKeys.Search];
        if (!searchResultUrl || !searchWord) {
            return undefined;
        }
        return `${searchResultUrl}?Search=${encodeURIComponent(searchWord)}`;
    };

    const handleClick = async (source?: string) => {
        if (!source) {
            return;
        }
        trackHelloRetailClick(source);
    };

    const decodedSearchTerm = useMemo(() => {
        try {
            return decodeURIComponent(searchTerm || '');
        } catch (error) {
            return searchTerm;
        }
    }, [searchTerm]);

    const typeHeadline = useMemo(() => {
        if (!hasTypeSelection) {
            return undefined;
        }
        switch (currentType) {
            case SearchContentTypes.Products:
                return `${formatString(
                    translate('search.searchResultPage.products'),
                    tabCount?.products || 0
                )} ${translate('search.searchResultPage.headerMatchesSearch')}`;
            case SearchContentTypes.Categories:
                return `${formatString(
                    translate('search.searchResultPage.categories'),
                    tabCount?.categories || 0
                )} ${translate('search.searchResultPage.headerMatchesSearch')}`;
            case SearchContentTypes.Series:
                return `${formatString(translate('search.searchResultPage.series'), tabCount?.series || 0)} ${translate(
                    'search.searchResultPage.headerMatchesSearch'
                )}`;
            case SearchContentTypes.BlogPost:
                return `${formatString(
                    translate('search.searchResultPage.articles'),
                    tabCount?.blogPost || 0
                )} ${translate('search.searchResultPage.headerMatchesSearch')}`;
            default:
                return undefined;
        }
    }, [hasTypeSelection, currentType, tabCount]);

    if (hasNoResults) {
        return (
            <Container gap="medium" column spacingBottom="medium">
                <Headline size={4}>
                    {`0 ${translate('search.searchResultPage.headerResultsFor')} `}
                    <Quote as="em">{`"`}</Quote>
                    <SearchTerm as="span">{decodedSearchTerm}</SearchTerm>
                    <Quote as="em">{`"`}</Quote>
                </Headline>
                <DesktopClose variant="custom" onClick={back}>
                    <SvgIcon svg="cross" size={24} />
                </DesktopClose>
            </Container>
        );
    }

    return (
        <Container gap="medium" column spacingBottom="medium">
            {!hasTypeSelection ? (
                <Headline size={4}>
                    {`${totalResults || ''} ${translate('search.searchResultPage.headerResultsFor')} `}
                    <Quote as="em">{`"`}</Quote>
                    <SearchTerm as="span">{decodedSearchTerm}</SearchTerm>
                    <Quote as="em">{`"`}</Quote>
                </Headline>
            ) : (
                <Headline size={4}>{typeHeadline}</Headline>
            )}
            <DesktopClose variant="custom" onClick={back}>
                <SvgIcon svg="cross" size={24} />
            </DesktopClose>
            {!noTypeResults && !hasTypeSelection && (
                <Flex gap="text" wrap="wrap">
                    <AnchorLinksContainer gap="text" wrap="wrap">
                        {!!tabCount?.products && (
                            <AnchorLink variant="link" onClick={() => scrollTo(resultListRefs?.products)}>{`${
                                tabCount.products
                            } ${translate('search.tabs.products')}`}</AnchorLink>
                        )}
                        {!!tabCount?.categories && (
                            <AnchorLink variant="link" onClick={() => scrollTo(resultListRefs?.categories)}>{`${
                                tabCount.categories
                            } ${translate('search.tabs.categories')}`}</AnchorLink>
                        )}
                        {!!tabCount?.series && (
                            <AnchorLink variant="link" onClick={() => scrollTo(resultListRefs?.series)}>{`${
                                tabCount.series
                            } ${translate('search.tabs.series')}`}</AnchorLink>
                        )}
                        {!!tabCount?.authors && (
                            <AnchorLink variant="link" onClick={() => scrollTo(resultListRefs?.authors)}>{`${
                                tabCount.authors
                            } ${translate('search.tabs.authors')}`}</AnchorLink>
                        )}
                        {!!tabCount?.blogPost && (
                            <AnchorLink variant="link" onClick={() => scrollTo(resultListRefs?.blogPost)}>{`${
                                tabCount.blogPost
                            } ${translate('search.tabs.blogPost')}`}</AnchorLink>
                        )}
                        {!!tabCount?.sitePages && (
                            <AnchorLink variant="link" onClick={() => scrollTo(resultListRefs?.sitePages)}>{`${
                                tabCount.sitePages
                            } ${translate('search.tabs.sitePages')}`}</AnchorLink>
                        )}
                    </AnchorLinksContainer>
                    <Text>{translate('search.searchResultPage.headerMatchesSearch')}</Text>
                </Flex>
            )}
            {!!filteredSearchWordSuggestions?.length && (
                <Flex gap="extrasmall" wrap="wrap">
                    <Text>{`${translate('search.searchResultPage.headerSearchingFor')}:`}</Text>
                    {filteredSearchWordSuggestions.map((suggestion, index) => (
                        <Link
                            themedLink
                            key={index}
                            replace
                            href={getSerchWordUrl(suggestion.title)}
                            onClick={() => handleClick(suggestion.trackingCode)}
                        >
                            {suggestion.title}
                        </Link>
                    ))}
                </Flex>
            )}
        </Container>
    );
};

const Container = styled(Flex)({
    position: 'relative',
});
const AnchorLinksContainer = styled(Flex)({});
const AnchorLink = styled(Button)(({ theme }) => ({
    fontWeight: theme.fontWeights.medium,
    '&:not(:last-child)': {
        '&:after': {
            content: '","',
        },
    },
}));

const Quote = styled(Text)(({ theme }) => ({
    color: theme.colors.grey60,
}));

const SearchTerm = styled(Text)(({ theme }) => ({
    color: theme.colors.grey60,
}));

const DesktopClose = styled(Button)(({ theme }) => ({
    display: 'none',
    [mq('frame')]: {
        display: 'flex',
        position: 'absolute',
        right: 0,
        top: 0,
        border: `1px solid ${theme.colors.black}`,
        borderRadius: theme.general.circleRadius,
        padding: theme.space[2],

        '&:hover': {
            backgroundColor: darken(theme.colors.white, 0.1),
            border: `1px solid ${theme.colors.black}`,
        },
    },
}));
